// The menu and the cart drawer.
//
// One file because they are the same object from opposite sides: a fixed panel,
// a backdrop, a focus trap, Escape to close, and the page behind held still.
// The differences are which side they come from and what they contain.
(function () {
  if (window.__pruvitPanels) return;
  window.__pruvitPanels = true;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  class Panel {
    constructor(el, backdrop) {
      this.el = el;
      this.backdrop = backdrop;
      this.returnTo = null;
    }

    /**
     * @param {Element} [trigger] - what to give focus back to on close.
     * @param {object} [opts]
     * @param {boolean} [opts.modal=true] - false for the desktop dropdown: the
     *   page stays scrollable, Tab is not trapped and focus does not move on
     *   open. A dropdown that stole focus from a hover would yank the page
     *   around under the pointer. The backdrop is NOT part of this: both modes
     *   show it, and CSS gives the dropdown's one a top edge at the bar.
     */
    open(trigger, opts) {
      const modal = !opts || opts.modal !== false;
      this.modal = modal;
      this.returnTo = trigger || document.activeElement;
      this.el.hidden = false;
      this.backdrop.hidden = false;

      // The element has to be in flow with its closed transform applied before
      // the open class can transition off it. Reading offsetWidth forces that
      // synchronously. requestAnimationFrame was the first attempt and it is
      // the wrong tool: frames are not scheduled in a background tab, so the
      // panel would open into the DOM and never slide in.
      void this.el.offsetWidth;
      this.el.classList.add('is-open');
      this.backdrop.classList.add('is-open');

      if (modal) {
        document.documentElement.style.overflow = 'hidden';
        // The first focusable may be display:none at this width — the region
        // button is hidden above 900px — and focusing a hidden element does
        // nothing at all, leaving focus behind on the page.
        const first = this.visible()[0];
        (first || this.el).focus({ preventScroll: true });
      }
      this.el.dispatchEvent(new CustomEvent('pruvit:panel:open'));
    }

    /** Move focus in after the fact — the keyboard and click paths into a
     *  dropdown want it, the hover path must not have it. */
    focusFirst() {
      const first = this.visible()[0];
      (first || this.el).focus({ preventScroll: true });
    }

    close(opts) {
      const keepFocus = opts && opts.keepFocus;
      this.el.classList.remove('is-open');
      this.backdrop.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      const done = () => {
        this.el.hidden = true;
        this.backdrop.hidden = true;
      };
      // Wait for the slide out, but never leave it open if the transition never
      // runs — a hidden tab, or reduced motion, both mean no transitionend.
      const timer = setTimeout(done, 450);
      this.el.addEventListener(
        'transitionend',
        () => {
          clearTimeout(timer);
          done();
        },
        { once: true }
      );
      // A hover close must not pull focus back to the button: the pointer has
      // simply moved on and the keyboard may be somewhere else entirely.
      if (!keepFocus && this.returnTo && typeof this.returnTo.focus === 'function') {
        this.returnTo.focus({ preventScroll: true });
      }
    }

    get isOpen() {
      return !this.el.hidden;
    }

    /** Focusable and actually rendered, in DOM order. */
    visible() {
      return [...this.el.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0
      );
    }

    /** Keep tab inside the panel while it is the only thing on screen. Not the
     *  dropdown, which is not the only thing on screen and whose last item
     *  should hand Tab back to the page. */
    trap(event) {
      if (this.modal === false) return;
      if (event.key !== 'Tab') return;
      const items = this.visible();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const menuEl = document.querySelector('[data-pruvit-menu]');
  const cartEl = document.querySelector('[data-pruvit-cart]');
  const menu = menuEl ? new Panel(menuEl, document.querySelector('[data-pruvit-menu-backdrop]')) : null;

  // The cart page (sections/pruvit-cart-page.liquid) is this drawer, moved
  // into the page and left open -- Craig, 2026-10-16: the cart page should
  // feel like the slide-in cart. Moving the one element rather than rendering
  // a second copy keeps everything below working unchanged on it: the lines,
  // savings, discount badges, subscription switch, quantity, the multi-buy
  // nudge, the add-ons rail, the checkout hand-off and the share sheet. On the
  // page it is not a dialog, so nothing traps focus or locks the scroll, and
  // the header's cart button scrolls to it instead of opening anything.
  const cartPage = cartEl && document.querySelector('[data-pruvit-cart-page]');
  if (cartPage) {
    cartPage.appendChild(cartEl);
    cartEl.classList.add('pruvit-cart--page', 'is-open');
    cartEl.hidden = false;
    cartEl.removeAttribute('aria-modal');
    cartEl.removeAttribute('tabindex');
    cartEl.setAttribute('role', 'region');
    // The page's heading. The drawer's title is an h3 among other headings;
    // here it is the only thing the page is about.
    const title = cartEl.querySelector('#pruvit-cart-title');
    if (title) {
      title.setAttribute('role', 'heading');
      title.setAttribute('aria-level', '1');
    }
  }
  const cart = !cartEl
    ? null
    : cartPage
    ? {
        open() {
          cartEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        close() {},
        get isOpen() {
          return false;
        },
      }
    : new Panel(cartEl, document.querySelector('[data-pruvit-cart-backdrop]'));

  // ---- the menu as a hover dropdown on desktop ----------------------------
  //
  // Craig, 2026-09-23. Hover OPENS it; it is never the only way in. Click still
  // works, Escape still closes, and the moment focus lands inside the panel the
  // hover rules stop applying — which is the part that matters here, because
  // the first thing in this panel is a search field with live results. A panel
  // that closed on mouse-leave would eat a half-typed query every time someone
  // let go of the mouse to type, and reaching for the keyboard is the normal
  // motion. Latching on focusin is what makes hover safe on a search surface.
  //
  // Gated on the pointer rather than the width, matching the CSS: >901px
  // includes touchscreen laptops and tablets with keyboards, which have no
  // hover and no Escape. They keep the drawer, its backdrop and its X.
  const ddQuery = window.matchMedia('(min-width: 901px) and (hover: hover) and (pointer: fine)');
  const menuTrigger = document.querySelector('[data-pruvit-menu-open]');
  const gnav = document.querySelector('.pruvit-gnav');

  const dropdown = {
    // Crossing the button on the way to the cart should not open a panel.
    // Leaving should not close one you are travelling towards; the panel hangs
    // off the header's bottom edge so there is no gap to cross, and this is
    // the forgiveness on top of that.
    OPEN_DELAY: 120,
    CLOSE_DELAY: 300,
    openTimer: null,
    closeTimer: null,
    latched: false,

    get on() {
      return !!(menu && menuTrigger && ddQuery.matches);
    },

    /** Measured, not assumed: the header slides under the referrer bar and the
     *  page gutter moves with the viewport. */
    place() {
      if (!menuTrigger) return;
      const b = menuTrigger.getBoundingClientRect();
      const bar = (gnav || menuTrigger).getBoundingClientRect();
      // On the root, not on the panel: the backdrop is the panel's SIBLING and
      // reads --dd-y for its own top edge, and a custom property set on one
      // sibling does not reach the other.
      const root = document.documentElement.style;
      root.setProperty('--dd-x', Math.round(b.left) + 'px');
      root.setProperty('--dd-y', Math.round(bar.bottom) + 'px');
      root.setProperty('--dd-max', Math.round(window.innerHeight - bar.bottom - 16) + 'px');
    },

    cancel() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.openTimer = this.closeTimer = null;
    },

    show(latched) {
      this.cancel();
      this.latched = !!latched;
      menu.el.classList.add('is-dd');
      if (menu.backdrop) menu.backdrop.classList.add('is-dd');
      this.place();
      if (!menu.isOpen) menu.open(menuTrigger, { modal: false });
      // The markup says aria-modal="true" because the drawer is one. This is
      // not: the page behind stays reachable by pointer and by Tab, and a
      // screen reader told otherwise would stop reading the rest of the page.
      menu.el.setAttribute('aria-modal', 'false');
      menuTrigger.setAttribute('aria-expanded', 'true');
    },

    hide(keepFocus) {
      this.cancel();
      this.latched = false;
      if (menu.isOpen) menu.close({ keepFocus: keepFocus });
      menu.el.setAttribute('aria-modal', 'true');
      menuTrigger.setAttribute('aria-expanded', 'false');
    },

    enter() {
      if (!this.on) return;
      clearTimeout(this.closeTimer);
      if (menu.isOpen) return;
      this.openTimer = setTimeout(() => this.show(false), this.OPEN_DELAY);
    },

    leave() {
      if (!this.on) return;
      clearTimeout(this.openTimer);
      // Latched means somebody is using it with the keyboard. The pointer
      // wandering off is not a decision to close.
      if (this.latched) return;
      this.closeTimer = setTimeout(() => this.hide(true), this.CLOSE_DELAY);
    },
  };

  if (menu && menuTrigger) {
    menuTrigger.setAttribute('aria-expanded', 'false');

    for (const el of [menuTrigger, menu.el]) {
      el.addEventListener('pointerenter', () => dropdown.enter());
      el.addEventListener('pointerleave', (event) => {
        // A pointer that left for the OTHER half of the pair has not left.
        const to = event.relatedTarget;
        if (to && (to === menuTrigger || menuTrigger.contains(to) || menu.el.contains(to))) return;
        dropdown.leave();
      });
    }

    // Anything reaching the panel by keyboard latches it open.
    menu.el.addEventListener('focusin', () => {
      if (dropdown.on && menu.isOpen) dropdown.latched = true;
      clearTimeout(dropdown.closeTimer);
    });

    // A click anywhere else is a decision. Pointerleave covers the pointer;
    // this covers the case where it never left, because the panel opened under
    // a stationary cursor and the visitor clicked the page behind it.
    document.addEventListener('pointerdown', (event) => {
      if (!dropdown.on || !menu.isOpen) return;
      if (menu.el.contains(event.target) || menuTrigger.contains(event.target)) return;
      dropdown.hide(true);
    });

    // Fixed to the viewport, anchored to something that moves in it.
    let frame = null;
    const reposition = () => {
      if (!dropdown.on || !menu.isOpen || frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        dropdown.place();
      });
    };
    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition, { passive: true });

    // Dragging a window between a laptop screen and a touch monitor, or just
    // resizing past 901px, must not leave the drawer wearing dropdown geometry.
    const onQuery = () => {
      if (menu.isOpen) dropdown.hide(true);
      menu.el.classList.remove('is-dd');
      if (menu.backdrop) menu.backdrop.classList.remove('is-dd');
    };
    if (ddQuery.addEventListener) ddQuery.addEventListener('change', onQuery);
    else if (ddQuery.addListener) ddQuery.addListener(onQuery);
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-pruvit-menu-open]')) {
      event.preventDefault();
      if (!menu) return;
      // Click is the keyboard's and the deliberate pointer's way in, so it
      // latches and takes focus. Clicking an already-open dropdown closes it,
      // which is what a button that says "Menu" should do when it is pressed.
      if (dropdown.on) {
        if (menu.isOpen && dropdown.latched) return dropdown.hide();
        dropdown.show(true);
        menu.focusFirst();
        return;
      }
      return menu.open(event.target.closest('[data-pruvit-menu-open]'));
    }
    if (event.target.closest('[data-pruvit-cart-open]')) {
      event.preventDefault();
      return cart && cart.open(event.target.closest('[data-pruvit-cart-open]'));
    }
    if (event.target.closest('[data-pruvit-menu-close], [data-pruvit-menu-backdrop]')) return menu && menu.close();
    if (event.target.closest('[data-pruvit-cart-close], [data-pruvit-cart-backdrop]')) return cart && cart.close();

    // The empty cart's CTA. Where its target is on this page — the landing
    // page's #choose — closing the drawer and scrolling beats navigating to
    // the same page and losing the drawer's own animation to a page load.
    // Everywhere else the link is just a link, which is why the href is real
    // rather than a hash the theme has to interpret.
    const emptyCta = event.target.closest('[data-pruvit-cart-empty-cta]');
    if (emptyCta) {
      const href = emptyCta.getAttribute('href') || '';
      const target = href.startsWith('#') && href.length > 1 ? document.getElementById(href.slice(1)) : null;
      if (!target) return;
      event.preventDefault();
      if (cart) cart.close();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  document.addEventListener('keydown', (event) => {
    // A modal dialog over a panel owns its own keys: Escape closes the dialog,
    // not the drawer behind it, and Tab is the dialog's to trap.
    if (event.target.closest && event.target.closest('dialog[open]')) return;
    const open = [menu, cart].find((p) => p && p.isOpen);
    if (!open) return;
    if (event.key === 'Escape') {
      // Through the dropdown, so the latch is cleared and aria-expanded is
      // told. Closing the panel directly would leave both saying it is open.
      if (open === menu && dropdown.on) return dropdown.hide();
      return open.close();
    }
    open.trap(event);
  });

  // ---- search -------------------------------------------------------------
  // Predictive search is an enhancement over a form that already submits, so a
  // failed request costs the visitor nothing: the field still works.
  const input = document.querySelector('[data-pruvit-search]');
  const results = document.querySelector('[data-pruvit-results]');
  const clear = document.querySelector('[data-pruvit-search-clear]');

  if (input && results) {
    let timer = null;
    let controller = null;

    // An EU guest gets no product suggestions: the gate cannot filter this
    // JSON, so it is not asked for (snippets/pruvit-menu.liquid). Enter still
    // goes to the search page, which gates.
    const held = input.hasAttribute('data-pruvit-search-held');

    const run = async (query) => {
      if (held) {
        results.innerHTML = '';
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`;
      try {
        const response = await fetch(url, { signal: controller.signal });
        const data = await response.json();
        const products = (data.resources?.results?.products) || [];
        results.innerHTML = products.length
          ? products
              .map(
                (p) => `
                <a class="pruvit-sres" href="${p.url}">
                  <span class="pruvit-sres__img">${p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="" width="44" height="44">` : ''}</span>
                  <span>
                    <span class="pruvit-sres__t">${p.title}</span>
                    <span class="pruvit-sres__m">${p.price ? p.price : ''}</span>
                  </span>
                </a>`
              )
              .join('')
          : `<p class="pruvit-sres__none">${pruvitT('searchNone', 'Nothing matched that. Try a flavour name.').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))}</p>`;
      } catch (e) {
        if (e.name !== 'AbortError') results.innerHTML = '';
      }
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      clear.hidden = !query;
      menuEl.classList.toggle('is-searching', query.length > 1);
      clearTimeout(timer);
      if (query.length < 2) return;
      timer = setTimeout(() => run(query), 220);
    });

    // The pills do not search on their own: they fill the box and hand it to
    // the listener above. One search path, so the debounce, the clear button
    // and the is-searching state all behave exactly as if it had been typed.
    const tags = document.querySelector('[data-pruvit-search-tags]');
    if (tags) {
      tags.addEventListener('click', (event) => {
        const tag = event.target.closest('[data-pruvit-search-tag]');
        if (!tag) return;
        input.value = tag.dataset.pruvitSearchTag;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    }

    clear.addEventListener('click', () => {
      input.value = '';
      clear.hidden = true;
      menuEl.classList.remove('is-searching');
      results.innerHTML = '';
      input.focus();
    });
  }

  // ---- cart ---------------------------------------------------------------
  if (cart) {
    /**
     * The decrease button at a quantity of one removes the line — that is what
     * it has always done, since the step lands on zero — so at one it says so.
     * A minus that empties the row is the same press wearing the wrong label.
     * The same trash the icon set draws, inline because this markup is built
     * in script rather than Liquid.
     */
    const TRASH =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 7h16M9 7V5h6v2M7 7l1 13h8l1-13"/></svg>';
    const decGlyph = (quantity) => (quantity === 1 ? TRASH : '&minus;');

    const body = cartEl.querySelector('[data-pruvit-cart-body]');
    const foot = cartEl.querySelector('[data-pruvit-cart-foot]');
    // Rendered only for a signed-in customer, so it is absent as often as not.
    const shareBtn = cartEl.querySelector('[data-pruvit-cart-share]');
    const sharePanel = cartEl.querySelector('[data-pruvit-share]');
    const total = cartEl.querySelector('[data-pruvit-cart-total]');
    const live = cartEl.querySelector('[data-pruvit-cart-live]');
    const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    // Amounts from /cart.js are net of VAT — Shopify's markets add tax at
    // checkout — while every price Liquid printed into this drawer already has
    // it. Adding it here, inside the one function every API amount passes
    // through, is what keeps a line price and a subtotal on the same footing.
    // The buy box's copy of money() must NOT do this: its inputs come from a
    // JSON blob Liquid already grossed, and a second pass would charge VAT
    // twice.
    //
    // Per line since 2026-10-05: each product carries its own Avalara tax
    // code, so a membership and a supplement are taxed differently and the
    // cart has one rate only when every line shares it. When they do, this is
    // exactly the old behaviour. When they do not — or any line has no rate
    // (an event ticket, a product with no code) — the drawer shows prices
    // before VAT and the VAT line says it comes at checkout, because one
    // inclusive total cannot honestly cover two rates.
    //
    // product id -> rate, or null for "quote none". Liquid seeds it with the
    // lines on the page; a product added later is asked through the
    // product.pruvit-vat view, once, by learnRate.
    const lineRates = (() => {
      try {
        return JSON.parse(cartEl.dataset.vatRates || '{}');
      } catch (e) {
        return {};
      }
    })();
    // product id -> [boxes, percent, ends] for a running multi-buy. Seeded by
    // Liquid for the lines already in the cart and learned with the VAT rate
    // for a product added later -- the same request, so no second fetch. A
    // product id with no entry has no offer.
    const lineOffers = (() => {
      try {
        return JSON.parse(cartEl.dataset.multibuy || '{}');
      } catch (e) {
        return {};
      }
    })();
    // variant id -> compare-at in cents, for variants priced below it. Seeded
    // and learned with the rates, like the offers. A one-time line strikes its
    // compare-at, the pack saving the buy box quotes ("20 · Save 5%"), which
    // /cart.js does not carry (Craig, 2026-10-27).
    const lineCompare = (() => {
      try {
        return JSON.parse(cartEl.dataset.compare || '{}');
      } catch (e) {
        return {};
      }
    })();
    const rateRequests = new Map();
    const learnRate = (item) => {
      const id = String(item.product_id);
      if (id in lineRates || !item.handle) return Promise.resolve();
      if (rateRequests.has(id)) return rateRequests.get(id);
      const request = fetch(`${root()}products/${item.handle}?view=pruvit-vat`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.text() : ''))
        .then((text) => {
          const parsed = JSON.parse(text);
          const rate = parsed.rate;
          lineRates[id] = typeof rate === 'number' && Number.isFinite(rate) ? rate : null;
          if (Array.isArray(parsed.multibuy)) lineOffers[id] = parsed.multibuy;
          if (parsed.compare && typeof parsed.compare === 'object') Object.assign(lineCompare, parsed.compare);
        })
        .catch(() => {
          // Unknown is not a rate. Quoting nothing is the side to fail on.
          lineRates[id] = null;
        });
      rateRequests.set(id, request);
      return request;
    };
    const ensureRates = (data) => Promise.all((data.items || []).map(learnRate));

    // The rate every line shares, or null. A line still being asked about is
    // left out rather than counted as "none": it is only ever the line an
    // optimistic add just drew, and the settled redraw that follows has it.
    const sharedRate = (items) => {
      let shared;
      for (const item of items) {
        const rate = lineRates[String(item.product_id)];
        if (rate === undefined) continue;
        if (rate === null) return null;
        if (shared === undefined) shared = rate;
        else if (shared !== rate) return null;
      }
      return shared === undefined ? null : shared;
    };

    let vatRate = 0;
    const gross = (cents) => (vatRate ? cents + Math.round((cents * vatRate) / 100) : cents);

    // The VAT sentence under the subtotal, rewritten with the rate it now
    // describes. Both sentences come from Liquid, translated, with %RATE%
    // where the number goes; nothing here composes copy.
    const paintVatLine = (shared) => {
      const line = cartEl.querySelector('[data-pruvit-vat-line]');
      if (!line) return;
      const sentence = cartEl.dataset.vatSentence || '';
      line.textContent =
        shared === null || !sentence ? cartEl.dataset.vatFallback || line.textContent : sentence.replace('%RATE%', String(shared));
    };

    // The same formatting rule as the buy box's money(). Kept as its own copy
    // because the cart is a header component with no module boundary to the
    // buy box, and two formatters that agree is better than one imported
    // across that line — but they must agree, so change both or neither.
    const money = (net, sample) => {
      const amount = gross(net);
      // Liquid's string is the shape, separators and all. Learn them from it
      // rather than assuming a point and no grouping: Germany renders this shop's
      // prices as €74,94, and rebuilding the number with toFixed put
      // "Save €11.24" beside it — a decimal point in a market that uses a
      // comma, in the one place a shopper is comparing two amounts.
      const digits = (sample.match(/\d[\d.,\u00a0\u202f\u2009 ]*\d|\d/) || ['0'])[0];
      const cents = /[.,]\d{2}$/.test(digits);
      const decimal = cents ? digits.slice(-3, -2) : '';
      // Whatever separators the sample used, minus the decimal one: what is left
      // is the thousands separator, and nothing left means it does not group.
      // Spaces count. French renders EUR as "1 234,56 €" with a
      // no-break space doing the grouping, and dropping it gave "1234,56 €".
      const seps = digits.replace(/\d/g, '');
      const group = seps.length > (cents ? 1 : 0) ? seps[0] : '';

      const parts = (amount / 100).toFixed(cents ? 2 : 0).split('.');
      let whole = parts[0];
      if (group) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
      return sample.replace(digits, whole + (parts[1] ? decimal + parts[1] : ''));
    };

    /**
     * Which variants have a selling plan, and which plan to offer.
     *
     * This used to read the buy box's rendered data, which was wrong in a way
     * that only showed off the home page: the cart is in the header and travels
     * with every page, the buy box does not. Anywhere else the map was empty,
     * so a line that came *off* SmartShip lost its switch and could not be put
     * back — the state was reachable and not leaveable.
     *
     * The cart asks the product instead. /products/{handle}.js carries the
     * plan groups and each variant's allocations, so the answer does not depend
     * on what else happens to be on the page. One request per product, cached,
     * and the buy box still seeds it for free when it is there.
     */
    const cartPlans = {};
    const planRequests = new Map();
    // Learned alongside cartPlans, from the same product lookup. requiredPlan:
    // variant id -> true when the product can only be bought on a plan (Pulse
    // Pro), so its line gets a label and no switch: switching it off is a
    // change Shopify refuses. planFrequency: plan id -> its Delivery
    // Frequency option, "Every year", which is how a membership's line says
    // what turning the switch on would do.
    const requiredPlan = {};
    const planFrequency = {};
    const frequencyOf = (plan) => {
      const option = ((plan && plan.options) || []).find((o) => /frequency/i.test(o.name || ''));
      return option && option.value ? String(option.value) : '';
    };

    const seedFromBuyBox = () => {
      const source = document.querySelector('[data-pruvit-buy-variants]');
      if (!source) return;
      try {
        for (const v of JSON.parse(source.textContent)) {
          const plan = v.plans.find((p) => p.segment === 'DEFAULT') || v.plans[0];
          if (plan) cartPlans[v.id] = plan.id;
        }
      } catch (e) {
        /* fall through to the product lookup */
      }
    };
    seedFromBuyBox();

    /**
     * The rail renders the plan each of its variants can take, so adding an
     * add-on never waits on a product lookup to know whether to draw a switch.
     * Unlike the buy box this is not a bet on page composition: the rail is
     * part of the cart, so it is there on every page the cart is.
     *
     * An empty data-plan is an answer, not a gap — the variant cannot be
     * subscribed — so it is recorded as null. Left undefined it would send
     * ensurePlans after a product that has already told us.
     */
    const seedFromRail = () => {
      for (const el of cartEl.querySelectorAll('[data-pruvit-addons] [data-plan]')) {
        const variant = Number(el.dataset.pruvitAddon || el.value);
        if (!variant) continue;
        cartPlans[variant] = Number(el.dataset.plan) || null;
      }
    };
    seedFromRail();

    const learnPlans = (handle) => {
      if (planRequests.has(handle)) return planRequests.get(handle);
      const request = fetch(`${root()}products/${handle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((product) => {
          if (!product) return;
          for (const variant of product.variants || []) {
            requiredPlan[variant.id] = Boolean(product.requires_selling_plan);
          }
          // The DEFAULT segment, same rule as the buy box: the storefront lists
          // every plan including rates that belong to existing and legacy
          // customers, and the cheapest is not the one a new customer gets.
          const segmentOf = (plan) => {
            const option = (plan.options || []).find((o) => o.name === 'Customer Segment');
            return option ? option.value : '';
          };
          const plans = (product.selling_plan_groups || []).flatMap((g) => g.selling_plans || []);
          const preferred = plans.find((p) => segmentOf(p) === 'DEFAULT') || plans[0];
          if (!preferred) return;
          planFrequency[preferred.id] = frequencyOf(preferred);
          for (const variant of product.variants || []) {
            const allowed = (variant.selling_plan_allocations || []).some(
              (a) => a.selling_plan_id === preferred.id
            );
            if (allowed) cartPlans[variant.id] = preferred.id;
          }
        })
        .catch(() => {
          /* the toggle simply does not render for this product */
        });
      planRequests.set(handle, request);
      return request;
    };

    /** Make sure every line in this cart knows whether it can be subscribed. */
    //
    // A line already on a plan is asked about too until we know whether the
    // plan is required: the buy box seeds cartPlans but not that, and a switch
    // on a required plan is one press away from an error.
    const ensurePlans = (data) => {
      const unknown = (data.items || [])
        .filter(
          (item) =>
            item.handle &&
            (cartPlans[item.variant_id] === undefined ||
              (item.selling_plan_allocation && requiredPlan[item.variant_id] === undefined) ||
              // A membership seeded by the buy box or rail knows its plan id
              // but not how often it renews, which its off label needs.
              (item.requires_shipping === false &&
                cartPlans[item.variant_id] &&
                planFrequency[cartPlans[item.variant_id]] === undefined))
        )
        .map((item) => item.handle);
      if (!unknown.length) return Promise.resolve();
      return Promise.all([...new Set(unknown)].map(learnPlans));
    };

    /**
     * The cart as it was last drawn. An optimistic add is built from this, so
     * it has to be whatever is on screen — provisional or settled.
     */
    let lastData = null;

    /**
     * `pending` is the set of line keys whose figures are not settled yet. The
     * lines render normally; only the money goes quiet, using the same states a
     * quantity change already uses.
     */
    // Declared above draw() rather than inside it: the empty state escapes its
    // settings too, and a const declared later in the same function body is in
    // the temporal dead zone, not merely undefined — it throws.
    const esc = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    const draw = (data, pending) => {
      lastData = data;
      // Whatever enforces a rule on the cart's contents (the Partner
      // Membership a pack needs, pruvit-partner-membership.js) acts on the
      // cart as drawn, so it sees every change this file sees -- on load, on
      // open, after any line change. Not while an add is still settling: that
      // draw is of a cart that no longer exists.
      if (!pending) {
        queueMicrotask(() => document.dispatchEvent(new CustomEvent('pruvit:cart:drawn', { detail: { cart: data } })));
      }
      const shared = sharedRate(data.items || []);
      vatRate = shared || 0;
      paintVatLine(shared);
      const sample = cartEl.dataset.moneyFormat || '$0.00';
      bubble(data.item_count);
      const addons = cartEl.querySelector('[data-pruvit-addons]');
      if (!data.item_count) {
        // The prototype's empty state, which had never been built: a bag, a
        // line saying what to do next, and the way to do it. What shipped was
        // the sentence on its own, which tells someone their cart is empty
        // and then abandons them in a drawer with a close button.
        //
        // The CTA's href is a setting because an empty cart can be opened
        // from any page. The landing page has a #choose anchor and the
        // handler below prefers it when it is on the page — that is the
        // prototype's behaviour — and anywhere else the link is followed
        // normally.
        const emptyCta = cartEl.dataset.emptyCtaLabel
          ? `<a class="pruvit-btn pruvit-btn--accent pruvit-cart__empty-cta" href="${esc(
              cartEl.dataset.emptyCtaHref || '#'
            )}" data-pruvit-cart-empty-cta>${esc(cartEl.dataset.emptyCtaLabel)}</a>`
          : '';
        body.innerHTML = `
          <div class="pruvit-cart__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5.4 8h13.2l-1.1 11.2a1.8 1.8 0 0 1-1.8 1.6H8.3a1.8 1.8 0 0 1-1.8-1.6L5.4 8Z"/>
              <path d="M9 8V6.2a3 3 0 0 1 6 0V8"/>
            </svg>
            <h4>${esc(cartEl.dataset.emptyText || pruvitT('cartEmpty', 'Your cart is empty'))}</h4>
            ${cartEl.dataset.emptyBody ? `<p>${esc(cartEl.dataset.emptyBody)}</p>` : ''}
            ${emptyCta}
          </div>`;
        foot.hidden = true;
        if (addons) addons.hidden = true;
        // Nothing to share, so nothing offering to. generateShareUrl returns
        // null for an empty cart anyway; hiding the control is what stops the
        // shopper finding that out by pressing it.
        if (shareBtn) shareBtn.hidden = true;
        if (sharePanel && !sharePanel.hidden) {
          sharePanel.hidden = true;
          if (shareBtn) shareBtn.setAttribute('aria-expanded', 'false');
        }
        return;
      }
      foot.hidden = false;
      if (addons) addons.hidden = false;
      if (shareBtn) shareBtn.hidden = false;

      // Boxes of each product across the cart. The offer counts the product,
      // not the line -- one Charged and one Caffeine Free already qualify -- so
      // the nudge asks for what the product is short of, and never appears on
      // a cart that already has the offer.
      const boxes = {};
      for (const item of data.items) boxes[item.product_id] = (boxes[item.product_id] || 0) + item.quantity;
      const nowSecs = Date.now() / 1000;

      body.innerHTML = data.items
        .map((item, index) => {
          const line = index + 1;

          // "Buy 2, save 30% · Add 1 more" (Craig, 2026-10-15). Tops up this
          // line, so the shopper gets more of what they chose.
          const offer = lineOffers[item.product_id];
          const short = offer && offer[2] > nowSecs ? offer[0] - boxes[item.product_id] : 0;
          const nudge = short > 0
            ? `<span class="pruvit-cline__mb">
                 <span>${esc(pruvitT('multibuyNudge', `Buy ${offer[0]}, save ${offer[1]}%`, { QTY: offer[0], PERCENT: offer[1] }))}</span>
                 <button type="button" data-mb-to="${item.quantity + short}">${esc(pruvitT('multibuyNudgeAdd', `Add ${short} more`, { COUNT: short }))}</button>
               </span>`
            : '';

          // Shopify applies discounts per line and tells us which, so the
          // badges are the real names and the real amount off — not a rule
          // restated in the theme that could drift from what is charged.
          const discounts = (item.line_level_discount_allocations || [])
            .map(
              (d) =>
                `<span class="pruvit-disc">${esc(d.discount_application.title)}</span>` +
                `<span class="pruvit-disc pruvit-disc--amt">&minus;${money(d.amount, sample)}</span>`
            )
            .join('');

          // The subscription toggle is only offered where the product actually
          // has a plan for this variant, and the plan it switches to is the one
          // Shopify says applies — never a hardcoded id.
          const plans = (item.selling_plan_allocation && [item.selling_plan_allocation]) || [];
          const onPlan = plans.length > 0;
          // What the label says. A shipped product keeps the plan's own name
          // when on and "Subscribe & save" when off — true of SmartShip, which
          // does save. Something not shipped is a membership: its plans renew
          // rather than deliver, and Partner's saves nothing, so "Subscribe &
          // save" was wrong as well as vague. Craig, 2026-10-05: say it
          // renews, and how often, from the plan's own frequency option.
          const renews = item.requires_shipping === false;
          const onFrequency = onPlan ? frequencyOf(item.selling_plan_allocation.selling_plan) : '';
          const offFrequency = planFrequency[cartPlans[item.variant_id]] || '';
          const planName = onPlan
            ? renews && onFrequency
              ? `Renews ${onFrequency.toLowerCase()}`
              : item.selling_plan_allocation.selling_plan.name
            : '';
          const offLabel = renews && offFrequency ? `Auto-renew ${offFrequency.toLowerCase()}` : 'Subscribe & save';
          const switchLabel = renews
            ? pruvitT('cartRenew', 'Renew automatically')
            : pruvitT('cartSubscribeItem', 'Subscribe and save on this item');
          // Only where a plan actually exists for this variant, or the line is
          // already on one. A switch that cannot switch is worse than no switch.
          //
          // Offered to someone who already holds a contract too, since
          // 2026-10-23: a partner building a cart to share needs it, and the
          // one-subscription rule is applied at checkout instead -- see the
          // guard below. It used to be hidden from holders, which left the
          // buy box's one-time-then-switch route as the way around the rule.
          //
          // A plan the product requires is stated, not offered: no switch.
          const toggle = onPlan && requiredPlan[item.variant_id]
            ? `<span class="pruvit-cline__ss">
                 <span class="pruvit-ss__lbl">${esc(planName)}</span>
               </span>`
            : onPlan || cartPlans[item.variant_id]
            ? `<span class="pruvit-cline__ss" data-ss-off="${esc(offLabel)}">
                 <button type="button" class="pruvit-ss" data-ss role="switch" aria-checked="${onPlan}" aria-label="${esc(switchLabel)}"><span class="pruvit-ss__knob"></span></button>
                 <span class="pruvit-ss__lbl">${onPlan ? esc(planName) : esc(offLabel)}</span>
               </span>`
            : '';

          // What the line would have cost. Shopify counts a subscription line's
          // plan price as its ORIGINAL price, so the line never read as
          // discounted: no strikethrough, and shoppers asked whether the 15%
          // was applying (Craig, 2026-10-19). The plan allocation carries the
          // one-time price as compare_at_price -- measured on K_1 Tropical 10:
          // $82.16 one-time, $69.84 on the plan, exactly the label's 15%.
          // That, not the variant's compare-at: K_1's $158.00 against $150.10
          // would strike a figure 16% off a plan that says 15, and stack a
          // "was" price on the subscription saving. No chip: the toggle's
          // label already says what the saving is.
          const planWas = onPlan && item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price
            ? item.selling_plan_allocation.compare_at_price * item.quantity
            : 0;
          // A one-time line strikes its variant's compare-at, as the buy box
          // does. Not a plan line: the paragraph above is why.
          const compareWas = !onPlan && lineCompare[String(item.variant_id)]
            ? lineCompare[String(item.variant_id)] * item.quantity
            : 0;
          const was = Math.max(item.original_line_price, planWas, compareWas);
          const struck = was > item.final_line_price
            ? `<s>${money(was, sample)}</s>`
            : '';

          // Line item properties, in the slot the variant text uses, because
          // for a bundle they ARE the variant: Biscuits Bundles records what
          // the customer picked as "Step 1", "Step 2" and so on, and a bundle
          // line without them is a title and a price with no way to tell what
          // is inside it. The live Concept theme shows them; this drawer did
          // not. Craig, 2026-09-28.
          //
          // A key beginning with an underscore is Shopify's convention for a
          // property an app keeps for itself, so those are skipped rather than
          // shown as machine text. Empty values are skipped too -- an app that
          // writes a key and no value has said nothing worth a line.
          //
          // Both halves are escaped. These strings are written by an app into
          // the cart and arrive here as JSON, so they are data, not markup.
          const props = Object.entries(item.properties || {})
            .filter(([k, v]) => k && k.charAt(0) !== '_' && v != null && String(v).trim() !== '')
            .map(
              ([k, v]) =>
                `<span class="pruvit-cline__p"><b>${esc(k)}:</b> ${esc(String(v))}</span>`
            )
            .join('');

          return `
          <div class="pruvit-cline" data-line="${line}" data-variant="${item.variant_id}" data-key="${esc(item.key)}">
            <span class="pruvit-cline__img">${item.image ? `<img src="${esc(item.image)}" alt="" width="64" height="64">` : ''}</span>
            <div>
              <span class="pruvit-cline__t">${esc(item.product_title)}</span>
              ${item.variant_title ? `<span class="pruvit-cline__v">${esc(item.variant_title)}</span>` : ''}
              ${props}
              ${toggle}
              ${discounts ? `<span class="pruvit-cline__discs">${discounts}</span>` : ''}
              ${nudge}
              <div class="pruvit-cline__row">
                <div class="pruvit-qty">
                  <button type="button" data-qty="-1" aria-label="${esc(item.quantity === 1 ? pruvitT('cartRemove', 'Remove') : pruvitT('cartDecrease', 'Decrease quantity'))}">${decGlyph(item.quantity)}</button>
                  <span data-qty-value>${item.quantity}</span>
                  <button type="button" data-qty="1" aria-label="${esc(pruvitT('cartIncrease', 'Increase quantity'))}">+</button>
                </div>
                <span class="pruvit-cline__price${struck ? ' is-saved' : ''}">${struck}${money(item.final_line_price, sample)}</span>
              </div>
            </div>
          </div>`;
        })
        .join('');
      total.textContent = money(data.total_price, sample);
      live.textContent = pruvitT(
        data.item_count === 1 ? 'cartCountOne' : 'cartCountOther',
        data.item_count === 1 ? '%COUNT% item, %TOTAL%' : '%COUNT% items, %TOTAL%',
        { COUNT: data.item_count, TOTAL: money(data.total_price, sample) }
      );

      // A line change in flight owns the foot too, so do not clear what it set.
      foot.classList.toggle('is-busy', Boolean(pending) || inFlight.size > 0);

      /**
       * The subtotal is withheld rather than dimmed. A quantity change dims,
       * because the figure on screen is settling towards something near it; the
       * subtotal during an add belongs to a cart that no longer exists, and
       * dimming a wrong number still shows a wrong number — people read the
       * figure, not the opacity.
       *
       * The line's own price is shown, dimmed. It comes from add.js, which
       * costs the line as the cart now holds it: measured here, MITO//PLEX
       * picked up a "P | New VIP Discount - 25%" worth $15.08 on entering the
       * cart and add.js reported the discounted figure, agreeing with cart.js
       * both times it was checked.
       *
       * What none of that gives us is the subtotal — add.js answers with the
       * line, not the cart, so there is no item_count and no total_price to
       * read. Adding them up here would be a guess wearing the same typeface as
       * a fact: discounts are allocated across the cart, so the sum of the
       * lines is not guaranteed to be the total, and a line already on screen
       * can be re-costed by the one being added.
       */
      total.classList.toggle('pruvit-unsettled', Boolean(pending));
      if (!pending) return;
      for (const row of body.querySelectorAll('[data-key]')) {
        if (!pending.has(row.dataset.key)) continue;
        row.classList.add('is-busy');
        row.setAttribute('aria-busy', 'true');
      }
    };

    // The header count is server-rendered by Horizon's cart-bubble, which only
    // knows what the cart held when the page was built. Now that this file owns
    // every change to the cart, it owns keeping that number honest too.
    const bubble = (count) => {
      for (const el of document.querySelectorAll('.pruvit-tool--cart .cart-bubble')) {
        // The count goes in Horizon's own span. Writing to the bubble's
        // textContent destroyed .cart-bubble__background and .cart-bubble__text
        // along with it, which is half of why the badge looked wrong.
        const slot = el.querySelector('.cart-bubble__text-count');
        if (slot) slot.textContent = count > 99 ? '99+' : String(count);
        el.classList.toggle('visually-hidden', count === 0);
        el.hidden = count === 0;
      }
    };

    /**
     * One cart fetch at a time, and who is asking decides what happens to a
     * second request while the first is still out.
     *
     * An add from the buy box caused two: the line hand-off asks for the cart,
     * and opening the drawer asks again. The second one only wants fresh data
     * and a fetch issued after the change already is fresh — so it joins.
     *
     * `after` marks the opposite case: a caller reporting a change that has
     * just happened. A request already in flight may have been read before that
     * change, so joining it could draw the cart as it was. Those ask again.
     */
    let loading = null;
    let reload = false;
    const load = (options) => {
      if (loading) {
        if (options && options.after) reload = true;
        return loading;
      }
      if (!body.childElementCount) body.classList.add('is-loading');
      loading = (async () => {
        try {
          const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
          const data = await response.json();
          await Promise.all([ensurePlans(data), ensureRates(data)]);
          draw(data);
        } catch (e) {
          body.innerHTML = `<p class="pruvit-cart__empty">${esc(pruvitT('cartLoadFailed', 'Could not load your cart.'))}</p>`;
        } finally {
          body.classList.remove('is-loading');
          loading = null;
          if (reload) {
            reload = false;
            load();
          }
        }
      })();
      return loading;
    };

    /**
     * Marks a line, and the totals, as working.
     *
     * The click has already been answered by then — the number has moved, the
     * toggle has flipped — so this is not "something is happening", it is "what
     * you asked for is not settled yet". Which is why it dims rather than
     * replacing anything with a spinner: the line is still readable, and the
     * figure that might yet change is the one carrying the state.
     */
    const setBusy = (row, on) => {
      if (row) {
        row.classList.toggle('is-busy', on);
        row.setAttribute('aria-busy', String(on));
      }
      foot.classList.toggle('is-busy', on);
    };

    /**
     * Redrawing replaces the whole body, so focus lands back on <body> and a
     * keyboard user loses their place mid-task — pressing + three times would
     * work once. The control is found again by the line it belongs to and what
     * it does.
     */
    const withFocusKept = (fn) => {
      const active = document.activeElement;
      const row = active && active.closest ? active.closest('[data-line]') : null;
      const key = row
        ? {
            line: row.dataset.line,
            qty: active.dataset ? active.dataset.qty : undefined,
            ss: active.hasAttribute && active.hasAttribute('data-ss'),
          }
        : null;
      fn();
      if (!key) return;
      const back = body.querySelector(`[data-line="${key.line}"]`);
      if (!back) return;
      const target = key.ss
        ? back.querySelector('[data-ss]')
        : key.qty !== undefined
        ? back.querySelector(`[data-qty="${key.qty}"]`)
        : null;
      if (target) target.focus({ preventScroll: true });
    };

    const inFlight = new Map();

    const change = async (line, quantity, sellingPlan, row) => {
      setBusy(row, true);
      try {
        const payload = { line };
        if (quantity !== null && quantity !== undefined) payload.quantity = quantity;
        // null is meaningful here and undefined is not: null takes the line off
        // its plan, where leaving the key out would keep whatever it had.
        if (sellingPlan !== undefined) payload.selling_plan = sellingPlan;
        const response = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || pruvitT('cartUpdateFailed', 'Could not update your cart.'));
        await Promise.all([ensurePlans(data), ensureRates(data)]);
        withFocusKept(() => draw(data));
        document.dispatchEvent(
          new CustomEvent('shopify:cart:lines-update', { detail: { action: 'update', source: 'pruvit-cart' } })
        );
      } catch (e) {
        // The optimistic state was a guess and the guess was wrong, so go back
        // to what the cart actually holds rather than leaving the guess up.
        live.textContent = e.message;
        await load({ after: true });
      } finally {
        setBusy(row, false);
        inFlight.delete(line);
      }
    };

    /**
     * Quantity is spammable in a way the plan toggle is not, so it coalesces:
     * every press moves the number at once and one request goes out with where
     * the reader landed. Blocking the stepper until each round trip finished
     * would be honest and horrible.
     */
    const queued = new Map();
    const commitQuantity = (line, row) => {
      clearTimeout(queued.get(line));
      queued.set(
        line,
        setTimeout(() => {
          queued.delete(line);
          const value = row.querySelector('[data-qty-value]');
          change(line, parseInt(value.textContent, 10), undefined, row);
        }, 320)
      );
    };

    body.addEventListener('click', (event) => {
      // The multi-buy nudge: the line's quantity, straight to what qualifies,
      // through the same path as the + button.
      const topUp = event.target.closest('[data-mb-to]');
      if (topUp) {
        const row = topUp.closest('[data-line]');
        if (row.classList.contains('is-busy')) return;
        row.querySelector('[data-qty-value]').textContent = topUp.dataset.mbTo;
        topUp.disabled = true;
        return commitQuantity(parseInt(row.dataset.line, 10), row);
      }

      const qty = event.target.closest('[data-qty]');
      if (qty) {
        const row = qty.closest('[data-line]');
        const value = row.querySelector('[data-qty-value]');
        const next = Math.max(0, parseInt(value.textContent, 10) + parseInt(qty.dataset.qty, 10));
        // Answer the press now. The number is the feedback; waiting for the
        // network to draw it is what made this feel broken.
        value.textContent = String(next);
        const dec = row.querySelector('[data-qty="-1"]');
        dec.disabled = next === 0;
        // The label has to move with the number, or the row spends the moment
        // between press and response offering the wrong action.
        dec.innerHTML = decGlyph(next);
        dec.setAttribute('aria-label', next === 1 ? pruvitT('cartRemove', 'Remove') : pruvitT('cartDecrease', 'Decrease quantity'));
        return commitQuantity(parseInt(row.dataset.line, 10), row);
      }

      const ss = event.target.closest('[data-ss]');
      if (ss) {
        const row = ss.closest('[data-line]');
        if (row.classList.contains('is-busy')) return;
        const on = ss.getAttribute('aria-checked') === 'true';
        const plan = cartPlans[row.dataset.variant];
        if (!on && !plan) return;

        // Flip it now, and say what it will be — the label is the sentence the
        // reader is checking, so it has to move with the switch.
        ss.setAttribute('aria-checked', String(!on));
        const label = row.querySelector('.pruvit-ss__lbl');
        // "Subscribing…" read as a commitment being made rather than a
        // setting being changed, which is an alarming thing to see on a button
        // you have just pressed.
        // Turning off shows the line's own off label — "Auto-renew every
        // year" on a membership — rather than SmartShip's.
        const wrap = ss.closest('[data-ss-off]');
        if (label) label.textContent = on ? (wrap && wrap.dataset.ssOff) || 'Subscribe & save' : 'Updating…';

        // The quantity goes with it. /cart/change.js defaults quantity to 1
        // when the key is absent, so changing only the selling plan silently
        // reset a line of three to a line of one.
        const quantity = parseInt(row.querySelector('[data-qty-value]').textContent, 10);
        return change(parseInt(row.dataset.line, 10), quantity, on ? null : plan, row);
      }
    });

    /**
     * One subscription per customer, applied at checkout (2026-10-23).
     *
     * Craig: partners build carts for their customers, so a SmartShip holder
     * can now put subscription lines in a cart -- in the buy box and with the
     * switch above -- and share it. The rule moved here, to the one moment the
     * cart is about the holder's own order: every SmartShip line goes to
     * one-time, the drawer redraws, and a dialog says what changed before
     * checkout carries on. Only shipped lines: a plan on something not shipped
     * is a membership, the same split the switch's label makes, and the
     * contract this checks for is SmartShip's alone (pruvit-has-subscription).
     *
     * Capture on window, so it runs before ShapeTech's handler on <body> and
     * before Selleasy's, both of which would otherwise send the cart on as it
     * stands. "Continue" clicks the real button again with `passed` set, and
     * from there checkout is the same path everyone else takes.
     *
     * Memberships too, since 2026-10-28 (Craig): a customer who already holds
     * the Partner/Promoter or Pulse Pro membership has that product's lines
     * taken out of their own order, by MV Qore's `_membership_active` tags
     * (pruvit-held-memberships). Holding one does not block the other.
     *
     * Then split, the same day (Craig): buying the membership you hold is how
     * a member renews -- the new term always stacks after the current expiry
     * -- so a held membership stays in the cart and the dialog asks, with
     * Continue or Remove. Only one that renews by itself (a live contract on
     * its own plan) still comes out, because buying it again would start a
     * second contract billed alongside the first.
     *
     * Theme-side, like the buy box rule it replaces: it closes the loophole in
     * this storefront, not in the checkout itself.
     */
    const guard = document.querySelector('[data-pruvit-subguard]');
    if (guard) {
      const list = guard.querySelector('[data-pruvit-subguard-list]');
      const guardTotal = guard.querySelector('[data-pruvit-subguard-total]');
      const guardErr = guard.querySelector('[data-pruvit-subguard-err]');
      const go = guard.querySelector('[data-pruvit-subguard-go]');
      const title = guard.querySelector('.pruvit-subguard__title');
      const lede = guard.querySelector('.pruvit-subguard__lede');
      const manage = guard.querySelector('.pruvit-subguard__manage');
      const review = guard.querySelector('.pruvit-subguard__review');
      const remove = guard.querySelector('[data-pruvit-subguard-remove]');
      let changed = [];
      let keptLines = [];
      let passed = false;
      let checking = false;
      let trigger = null;

      const onSmartShip = (item) => Boolean(item.selling_plan_allocation) && item.requires_shipping !== false;

      const proceed = () => {
        passed = true;
        trigger.click();
      };

      const settle = (button) => {
        button.removeAttribute('loading');
        button.removeAttribute('aria-busy');
      };

      const change = async (payload) => {
        const response = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const next = await response.json();
        if (!response.ok) throw new Error(next.description || next.message || '');
        return next;
      };

      window.addEventListener(
        'click',
        async (event) => {
          const button = event.target.closest && event.target.closest('.pruvit-cart__checkout');
          if (!button) return;
          if (passed) {
            passed = false;
            return;
          }
          // Read now rather than at load, so the flags are the drawer's
          // current answer and a test can set them.
          const holdsSmartShip = cartEl.dataset.hasSubscription === '1';
          const heldMemberships = (cartEl.dataset.heldMemberships || '').split(',').filter(Boolean);
          const renewingMemberships = (cartEl.dataset.renewingMemberships || '').split(',').filter(Boolean);
          if (!holdsSmartShip && !heldMemberships.length) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (checking) return;
          checking = true;
          trigger = button;
          // The same "taking you to checkout" state ShapeTech's handler shows,
          // so the press is answered while the cart is read.
          button.setAttribute('loading', '');
          button.setAttribute('aria-busy', 'true');
          let switched = [];
          let removed = [];
          let kept = [];
          try {
            // Fresh, not lastData: this decides what the order contains, and
            // another app may have touched the cart since the last draw.
            let data = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
            const items = data.items || [];
            switched = holdsSmartShip ? items.filter(onSmartShip) : [];
            // Every line of a membership that renews by itself, on its plan or
            // not: Partner can be bought either way, and Pulse Pro only on a
            // plan, so there is nothing to switch it to -- it comes out. A
            // held membership that does not renew stays: that is a renewal.
            const product = (item) => String(item.product_id);
            removed = items.filter((item) => renewingMemberships.includes(product(item)));
            kept = items.filter(
              (item) => heldMemberships.includes(product(item)) && !renewingMemberships.includes(product(item))
            );
            if (!switched.length && !removed.length && !kept.length) {
              settle(button);
              checking = false;
              return proceed();
            }
            // One at a time, by key. A switched line keeps its quantity,
            // because change.js resets a line to one without it -- the same
            // trap the switch above notes.
            for (const item of removed) data = await change({ id: item.key, quantity: 0 });
            for (const item of switched) {
              data = await change({ id: item.key, quantity: item.quantity, selling_plan: null });
            }
            if (switched.length || removed.length) {
              await Promise.all([ensurePlans(data), ensureRates(data)]);
              draw(data);
              document.dispatchEvent(
                new CustomEvent('shopify:cart:lines-update', { detail: { action: 'update', source: 'pruvit-cart' } })
              );
            }
            showGuard(data, switched, removed, kept);
          } catch (e) {
            // Nothing is sent on with a half-changed cart. The drawer is
            // redrawn from what the cart now holds and checkout waits for
            // another press.
            await load({ after: true });
            showGuard(null, switched, removed, kept);
            guardErr.textContent = guard.dataset.failedText;
            guardErr.hidden = false;
          } finally {
            settle(button);
            checking = false;
          }
        },
        true
      );

      /**
       * What changed, by line. A switched line shows the price it now costs --
       * not the plan price struck beside it: a strikethrough says "you are
       * paying less", and there the figure has gone up. A removed membership
       * shows its price struck, which is exactly what it says: not charged.
       * A kept one shows its price as it stands, since it is still being
       * bought.
       *
       * Switched lines are matched by variant and properties rather than key:
       * the key includes the plan, so the line that comes back is a new key,
       * and it may have merged into a one-time line of the same item already
       * in the cart.
       */
      const showGuard = (data, switched, removed, kept) => {
        const sample = cartEl.dataset.moneyFormat || '$0.00';
        const once = guard.dataset.onceText || '';
        const same = (a, b) =>
          a.variant_id === b.variant_id && JSON.stringify(a.properties || {}) === JSON.stringify(b.properties || {});
        const row = (item, note, price) => `
            <li class="pruvit-subguard__li">
              <span class="pruvit-subguard__img">${item.image ? `<img src="${esc(item.image)}" alt="" width="48" height="48">` : ''}</span>
              <span class="pruvit-subguard__what">
                <span class="pruvit-subguard__t">${esc(item.product_title)}${item.quantity > 1 ? ` <span class="pruvit-subguard__q">&times;${item.quantity}</span>` : ''}</span>
                <span class="pruvit-subguard__v">${esc(note)}</span>
              </span>
              <span class="pruvit-subguard__p">${price}</span>
            </li>`;
        list.innerHTML =
          removed.map((was) => row(was, guard.dataset.removedText || '', `<s>${money(was.final_line_price, sample)}</s>`)).join('') +
          switched
            .map((was) => {
              const now = ((data && data.items) || []).find((item) => !item.selling_plan_allocation && same(item, was)) || was;
              const note = [was.variant_title, once].filter(Boolean).join(' · ');
              return row(was, note, money(Math.round((now.final_line_price / now.quantity) * was.quantity), sample));
            })
            .join('') +
          kept.map((item) => row(item, guard.dataset.keptText || '', money(item.final_line_price, sample))).join('');
        changed = switched.map((item) => String(item.variant_id));
        keptLines = kept;
        const altered = Boolean(switched.length || removed.length);
        // Which it is: a subscription, a membership, or both. The title names
        // what they hold; the lede says what was done, then, if a membership
        // was left in, what buying it again means.
        const d = guard.dataset;
        if (switched.length && (removed.length || kept.length)) title.textContent = d.bothTitle;
        else if (switched.length) title.textContent = d.subTitle;
        else title.textContent = d.memberTitle;
        let done = '';
        if (switched.length && removed.length) done = d.bothBody;
        else if (switched.length) done = d.subBody;
        else if (removed.length) done = removed.length > 1 ? d.memberBodyOther : d.memberBodyOne;
        lede.textContent = [done, kept.length ? d.keptBody : ''].filter(Boolean).join(' ');
        // Managing a contract is the other thing a holder may have meant:
        // these items in the delivery they already get, or the renewal that
        // is already set up. A membership left in has no contract to manage.
        if (manage) manage.hidden = !altered;
        // The lede says the lines were changed, which a failed run cannot
        // promise -- some may have been and some not. The drawer behind
        // shows which.
        lede.hidden = !data;
        list.hidden = !data;
        // "New subtotal" only when something changed it.
        guardTotal.parentElement.hidden = !data || !altered;
        if (data) guardTotal.textContent = money(data.total_price, sample);
        guardErr.hidden = true;
        guard.classList.toggle('is-error', !data);
        // Nothing left to check out once a membership came out of a cart that
        // held only that: Review is the one way on.
        go.hidden = !data || !data.item_count;
        // A membership left in is a question -- renewing, or a mistake? --
        // so the second button answers it instead of offering a review.
        remove.hidden = !data || !kept.length;
        review.hidden = !remove.hidden;
        remove.removeAttribute('loading');
        // Escape closes without setting a return value, so a "go" from an
        // earlier run would otherwise read as this one's answer.
        guard.returnValue = '';
        if (!guard.open) guard.showModal();
        // Onto the primary action, not the close button showModal() picks:
        // carrying on is the likely answer and Enter should give it.
        (go.hidden ? review : go).focus({ preventScroll: true });
      };

      go.addEventListener('click', () => {
        guard.close('go');
        proceed();
      });

      // Take the kept membership out and go back to the cart, not on to
      // checkout: what is left may not be an order they meant on its own. The
      // next press finds nothing to ask about and goes straight through.
      remove.addEventListener('click', async () => {
        if (remove.hasAttribute('loading')) return;
        remove.setAttribute('loading', '');
        remove.setAttribute('aria-busy', 'true');
        try {
          let data = null;
          for (const item of keptLines) data = await change({ id: item.key, quantity: 0 });
          if (data) {
            await Promise.all([ensurePlans(data), ensureRates(data)]);
            draw(data);
            document.dispatchEvent(
              new CustomEvent('shopify:cart:lines-update', { detail: { action: 'update', source: 'pruvit-cart' } })
            );
          }
          keptLines = [];
          guard.close('removed');
        } catch (e) {
          await load({ after: true });
          guardErr.textContent = guard.dataset.failedText;
          guardErr.hidden = false;
        } finally {
          settle(remove);
        }
      });

      // Review -- the X, the button, Escape or a click outside: close, and
      // point at the lines that changed. They are marked for a moment rather
      // than left to be found, since nothing else about a one-time line says
      // it was a subscription a second ago.
      for (const el of guard.querySelectorAll('[data-pruvit-subguard-review]')) {
        el.addEventListener('click', () => guard.close('review'));
      }
      guard.addEventListener('close', () => {
        if (guard.returnValue === 'go') return;
        const rows = [...body.querySelectorAll('.pruvit-cline')].filter((row) => changed.includes(row.dataset.variant));
        if (rows[0]) {
          rows[0].scrollIntoView({
            block: 'nearest',
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
        for (const row of rows) {
          row.classList.remove('is-changed');
          void row.offsetWidth;
          row.classList.add('is-changed');
          row.addEventListener('animationend', () => row.classList.remove('is-changed'), { once: true });
        }
        if (trigger) trigger.focus({ preventScroll: true });
      });
      // A click on the dimmed page outside the panel is a "not yet".
      guard.addEventListener('click', (event) => {
        if (event.target === guard) guard.close('review');
      });
    }

    /**
     * Draw the line Shopify just confirmed, without waiting to be told the rest
     * of the cart.
     *
     * add.js answers with the line as it now stands — its key, its resulting
     * quantity, its price — which is everything a row needs. What it does not
     * answer with is the cart: no item_count, no total_price. So the line goes
     * up straight away and the totals stay as they were, marked unsettled,
     * until cart.js says what they are.
     *
     * The subtotal is deliberately not calculated here — see draw(), which
     * withholds it until cart.js says what it is.
     */
    const drawAdded = (items) => {
      if (!lastData || !items.length) return null;
      const next = { ...lastData, items: [...(lastData.items || [])] };
      const pending = new Set();
      for (const item of items) {
        pending.add(item.key);
        const at = next.items.findIndex((line) => line.key === item.key);
        // add.js returns the line's resulting quantity, not the amount added,
        // so an existing line is replaced rather than incremented.
        if (at >= 0) next.items[at] = { ...next.items[at], ...item };
        else next.items.push(item);
      }
      next.item_count = next.items.reduce((n, line) => n + line.quantity, 0);
      // Asked now, so the settled redraw after cart.js does not wait on it.
      ensureRates({ items });
      draw(next, pending);

      // The line may be below the fold of a cart that was already long, and a
      // change you cannot see has not been communicated.
      // Only worth doing in a drawer that is already open. One opening from an
      // add starts at the top of the cart, which is where a reader expects to
      // begin, and scrolling a panel nobody is looking at can move the page.
      const row = cartEl.classList.contains('is-open')
        ? body.querySelector(`[data-key="${CSS.escape(items[0].key)}"]`)
        : null;
      if (row) {
        row.scrollIntoView({
          block: 'nearest',
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
      live.textContent = pruvitT('cartAdded', '%TITLE% added. Updating your total.', { TITLE: items[0].product_title });
      return pending;
    };

    const add = async (id) => {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      const payload = await response.json().catch(() => null);

      // A sold-out variant or an order-limit app answers 422 here. Drawing an
      // optimistic line for a line that was refused would be a lie that only
      // corrected itself two seconds later.
      if (!response.ok) {
        live.textContent = (payload && (payload.description || payload.message)) || pruvitT('addFailed', 'Could not add that.');
        await load({ after: true });
        return;
      }

      drawAdded((payload && payload.items) || []);

      // The button is free from here: the row is on screen and the rest is
      // reconciliation. Errors are load()'s to show.
      load({ after: true }).then(() => {
        document.dispatchEvent(
          new CustomEvent('shopify:cart:lines-update', { detail: { action: 'add', source: 'pruvit-cart' } })
        );
      });
    };

    // The rail lives outside the scrolling body, so its clicks need their own
    // listener rather than riding the body's.
    const addonsEl = cartEl.querySelector('[data-pruvit-addons]');
    if (addonsEl) {
      addonsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pruvit-addon]');
        if (!button || button.hasAttribute('disabled')) return;
        button.setAttribute('disabled', '');
        button.classList.add('is-busy');
        add(button.dataset.pruvitAddon).finally(() => {
          button.removeAttribute('disabled');
          button.classList.remove('is-busy');
        });
      });

      // The button is the price: choosing an option has to move both the figure
      // shown and the variant the button would add, or the two disagree and the
      // cart is the first place anyone finds out.
      addonsEl.addEventListener('change', (event) => {
        const select = event.target.closest('[data-pruvit-addon-variants]');
        if (!select) return;
        const card = select.closest('.pruvit-acard');
        const button = card && card.querySelector('[data-pruvit-addon]');
        const option = select.options[select.selectedIndex];
        if (!button || !option) return;
        button.dataset.pruvitAddon = option.value;
        const price = button.querySelector('[data-pruvit-addon-price]');
        if (price && option.dataset.price) price.textContent = option.dataset.price;
      });
    }

    cartEl.addEventListener('pruvit:panel:open', () => load());
    // Once on load, so a returning visitor's count is right before they touch
    // anything — the server-rendered number can be a cached page's.
    load();
    // Anything that changes the cart elsewhere — the buy box, an app — should
    // be reflected the next time this opens, and immediately if it is already.
    document.addEventListener('shopify:cart:lines-update', (event) => {
      const source = event.detail && event.detail.source;
      // Both of ours are already handled: the cart's own changes draw as they
      // happen, and the buy box hands its line over on pruvit:cart:added.
      if (source === 'pruvit-cart' || source === 'pruvit-buy') return;
      // Someone else changed the cart, so this is a change already made.
      load({ after: true });

      // Except Horizon's own product form -- the add button on the bundle
      // templates (Dual Pack, Build Your Own). It dispatches this BEFORE the
      // request, carrying the outcome as event.promise (the payload lives on
      // the event itself, not on detail), and nothing after it. So the drawer
      // read an empty cart and was never opened. On the Dual Pack it is
      // starker: EG Auto Add to Cart holds the add behind its Partner
      // Membership prompt, and releases it only when that is answered
      // (measured 2026-10-20: add.js pending until Close, then 200). Wait for
      // the outcome, then draw and open, as the buy box does for its own adds.
      if (event.action === 'add' && event.promise && typeof event.promise.then === 'function') {
        // And say so on the button meanwhile. Horizon plays its "Added" tick
        // on the click itself and reverts after 800ms, so through the
        // membership prompt and the seconds after it the button looked idle
        // (Craig, 2026-10-20). Our buy box's wording and treatment.
        const busy = event.target && event.target.querySelectorAll
          ? [...event.target.querySelectorAll('.add-to-cart-button')]
          : [];
        busy.forEach((button) => {
          button.setAttribute('data-pruvit-busy', pruvitT('addBusy', 'Adding…'));
          button.setAttribute('aria-busy', 'true');
        });
        const idle = () =>
          busy.forEach((button) => {
            button.removeAttribute('data-pruvit-busy');
            button.removeAttribute('aria-busy');
          });
        // Drawn before it opens, not after (Craig, 2026-10-20): opening first
        // slid in an empty drawer that filled a second later. load() hands
        // back a load already in flight and queues another after it, so wait
        // until none is running -- the line is in the drawer by then. The
        // button stays "Adding…" through the wait, so it still reads as work.
        const drawn = async () => {
          await load({ after: true });
          while (loading) await loading;
        };
        event.promise.then(
          async (result) => {
            const outcome = (result && result.detail) || {};
            await drawn();
            idle();
            if (outcome.didError) return;
            // The shared-cart page is on its way to /cart; nothing to open.
            if (document.querySelector('[data-pruvit-share-holder]')) return;
            if (cart && !cart.isOpen) cart.open(document.querySelector('[data-pruvit-cart-open]'));
          },
          () => {
            idle();
            load({ after: true });
          }
        );
      }
    });

    // The buy box has already been told what it added. Drawing it here costs
    // nothing and saves the drawer opening on an empty body while it asks.
    document.addEventListener('pruvit:cart:added', (event) => {
      const items = (event.detail && event.detail.items) || [];
      drawAdded(items);
      load({ after: true });
    });
  }
})();
